/**
 * A CLEANER WHO CANNOT COME (ops #85, `docs/plan-call-off.md` section 12), on the office's view of one
 * visit. Before anything is recorded: a form, the cleaner from the visit's own crew, the one word they
 * gave, how the office heard and when, and a note. Once recorded and while the client has not chosen:
 * what was heard, and the three choices the client has, each opening the move flow with the call-off
 * carried, and the way to withdraw a call-off made in error. After the choice: what became of it.
 *
 * Recording it tells the office and the owners and moves nothing (binding rule 7): the visit stays
 * where it is until the client's choice is recorded through the move flow and they have been told.
 * Nothing here is sent to anybody, and the cleaner's view of the visit shows none of it.
 */
import { useState, type FormEvent } from 'react';
import { CALL_OFF_REASONS, type CallOffReason, type CallOffReportedVia } from '@fk/shared';
import {
  ApiError,
  recordCallOff,
  withdrawCallOff,
  type CallOffView,
  type CrewOnVisit,
} from '../../api.ts';
import { Card } from '../../components/AppShell.tsx';
import { Button, SelectField, TextAreaField } from '../../components/Field.tsx';
import { ErrorNotice } from '../../components/Notice.tsx';
import { BUSINESS_TIME_ZONE, isTimeOfDay, orNull } from '../../format.ts';
import type { CallOffChoiceStart } from './MoveFlow.tsx';
import { TimeField } from './MoveFields.tsx';

const HEARD_BY: readonly Exclude<CallOffReportedVia, 'app'>[] = ['phone', 'text', 'in_person'];
const HEARD_BY_LABELS: Record<Exclude<CallOffReportedVia, 'app'>, string> = {
  phone: 'By phone',
  text: 'By text',
  in_person: 'In person',
};

/** What became of a closed call-off, in a sentence. */
function choiceWords(callOff: CallOffView): string {
  switch (callOff.choice) {
    case 'moved':
      return 'The client chose a new slot.';
    case 'substitute':
      return `The client chose ${callOff.substituteName ?? 'a named substitute'} to stand in.`;
    case 'skipped':
      return 'The client chose to skip the visit, at no charge.';
    case 'visit_cancelled':
      return 'The visit came off the calendar another way.';
    default:
      return 'The client has not chosen yet.';
  }
}

/** The instant of a time of day in Florida on a day, as the server stores an instant. */
export function floridaInstant(day: string, hhmm: string): string {
  const guess = new Date(`${day}T${hhmm}:00.000Z`);
  const zone =
    new Intl.DateTimeFormat('en-US', { timeZone: BUSINESS_TIME_ZONE, timeZoneName: 'shortOffset' })
      .formatToParts(guess)
      .find((part) => part.type === 'timeZoneName')?.value ?? 'GMT';
  const found = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(zone);
  const minutes = found
    ? (found[1] === '-' ? -1 : 1) * (Number(found[2]) * 60 + Number(found[3] ?? 0))
    : 0;
  return new Date(guess.getTime() - minutes * 60_000).toISOString();
}

export function CallOffCard({
  visitId,
  today,
  crew,
  callOff,
  open,
  onChoose,
  onReload,
}: {
  visitId: string;
  today: string;
  crew: readonly CrewOnVisit[];
  /** The open call-off, or the latest closed one, or null. */
  callOff: CallOffView | null;
  /** Whether the visit can still take a call-off: planned or confirmed. */
  open: boolean;
  onChoose: (start: CallOffChoiceStart) => void;
  onReload: () => void;
}) {
  if (callOff && callOff.choice === null) {
    return <OpenCallOff callOff={callOff} onChoose={onChoose} onReload={onReload} />;
  }
  return (
    <div className="mb-4">
      {callOff ? (
        <Card className="mb-4" label="A cleaner could not come">
          <h2 className="font-serif text-lg">Could not come</h2>
          <p className="mt-1 text-sm">
            {callOff.cleanerName}: {callOff.reason}, {callOff.howEarly}. {choiceWords(callOff)}
          </p>
        </Card>
      ) : null}
      {open && crew.length > 0 ? (
        <RecordCallOff visitId={visitId} today={today} crew={crew} onReload={onReload} />
      ) : null}
    </div>
  );
}

function OpenCallOff({
  callOff,
  onChoose,
  onReload,
}: {
  callOff: CallOffView;
  onChoose: (start: CallOffChoiceStart) => void;
  onReload: () => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function withdraw() {
    setBusy(true);
    setProblem(null);
    try {
      await withdrawCallOff(callOff.id);
      onReload();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'It was not withdrawn.');
      setBusy(false);
    }
  }

  return (
    <div
      className="mb-4 rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-4"
      data-call-off
    >
      <h2 className="font-serif text-lg">Cannot come</h2>
      <p className="mt-1 text-sm">
        {callOff.cleanerName} cannot come: {callOff.reason}, {callOff.howEarly}.
      </p>
      {callOff.afterStart ? (
        <p className="mt-1 text-sm">
          Heard after the visit should have started, so a new slot is free under the published
          policy.
        </p>
      ) : null}
      {callOff.note ? <p className="mt-1 text-sm text-navy/75">{callOff.note}</p> : null}
      <p className="mt-2 text-sm text-navy/75">
        Call the client and record what they chose. The price does not change, and a skipped visit
        is not charged. The visit stays where it is until they have been told.
      </p>
      {problem ? (
        <div className="mt-3">
          <ErrorNotice title="Not withdrawn" message={problem} />
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => onChoose({ id: callOff.id, kind: 'moved' })}>New slot</Button>
        <Button onClick={() => onChoose({ id: callOff.id, kind: 'cleaner_changed' })}>
          Substitute
        </Button>
        <Button onClick={() => onChoose({ id: callOff.id, kind: 'cancelled_by_us' })}>Skip</Button>
        <Button tone="quiet" onClick={withdraw} disabled={busy}>
          {busy ? 'Withdrawing' : 'Withdraw'}
        </Button>
      </div>
    </div>
  );
}

function RecordCallOff({
  visitId,
  today,
  crew,
  onReload,
}: {
  visitId: string;
  today: string;
  crew: readonly CrewOnVisit[];
  onReload: () => void;
}) {
  const [cleanerId, setCleanerId] = useState(crew.length === 1 ? crew[0]!.cleanerId : '');
  const [reason, setReason] = useState<CallOffReason | ''>('');
  const [heardBy, setHeardBy] = useState<Exclude<CallOffReportedVia, 'app'>>('phone');
  const [heardTime, setHeardTime] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    if (!cleanerId || !reason) {
      setProblem('Say who cannot come and the reason they gave.');
      return;
    }
    if (heardTime !== '' && !isTimeOfDay(heardTime)) {
      setProblem('The time it was heard is a time of day, like 18:10.');
      return;
    }
    setBusy(true);
    try {
      await recordCallOff(visitId, {
        cleanerId,
        reason,
        reportedVia: heardBy,
        heardAt: heardTime ? floridaInstant(today, heardTime) : null,
        note: orNull(note),
      });
      onReload();
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'It was not recorded.');
      setBusy(false);
    }
  }

  return (
    <details className="rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="cursor-pointer font-serif text-lg">A cleaner cannot come</summary>
      <form onSubmit={onSubmit} className="mt-3">
        <p className="mb-3 text-sm text-navy/75">
          The office and the owners are told at once, with a follow-up due today. Nothing moves and
          nobody is messaged until you record what the client chose.
        </p>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={problem} />
          </div>
        ) : null}
        <SelectField
          id="call-off-cleaner"
          label="Who cannot come"
          required
          value={cleanerId}
          onChange={setCleanerId}
          options={crew.map((one) => one.cleanerId)}
          toLabel={(id) => crew.find((one) => one.cleanerId === id)?.displayName ?? id}
          placeholder="Choose who"
        />
        <SelectField
          id="call-off-reason"
          label="The reason they gave"
          required
          value={reason}
          onChange={setReason}
          options={CALL_OFF_REASONS}
          toLabel={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
          placeholder="Choose one"
        />
        <SelectField
          id="call-off-heard-by"
          label="How the office heard"
          value={heardBy}
          onChange={setHeardBy}
          options={HEARD_BY}
          toLabel={(value) => HEARD_BY_LABELS[value]}
        />
        <TimeField
          id="call-off-heard-at"
          label="Heard today at (leave empty for now)"
          value={heardTime}
          onChange={setHeardTime}
        />
        <TextAreaField
          id="call-off-note"
          label="A note for the office"
          value={note}
          onChange={setNote}
          rows={2}
          hint="For the office alone. The client and the crew never read it."
        />
        <Button type="submit" disabled={busy} full>
          {busy ? 'Recording' : 'Record that they cannot come'}
        </Button>
      </form>
    </details>
  );
}
